import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { every, fill, isEqual, isNil, isUndefined, some } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import {
  DropdownHorizontalAlignmentsEnum,
  DropdownVerticalAlignmentsEnum,
} from '../../../shared/components/dropdown/dropdown.component';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { CustomFormFieldComponent } from '../custom-form-field/custom-form-field.component';
import { MultiSelectFieldBuilder } from './multi-select-field.builder';
import { IMultiSelectOption } from './multi-select-field.interface';
import { filter, takeUntil } from 'rxjs/operators';
import { I2gNgTrackingService } from 'i2g-ng-tracking';

@Component({
  selector: 'app-multi-select-field',
  templateUrl: 'multi-select-field.component.html',
  styleUrls: ['multi-select-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true,
    },
    MultiSelectFieldBuilder,
  ],
})
export class MultiSelectFieldComponent
  extends CustomFormFieldComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() condensed: boolean;
  @Input() placeholder: string;
  @Input() selectAllLabel: string;
  @Input() selectAllTrackingValue: string;
  @Input() values: List<IMultiSelectOption>;
  @Input() defaultValue: any;
  @Input() blocker: (
    selectOption: IMultiSelectOption
  ) => Promise<IMultiSelectOption>;
  @Input() icon: IIcon;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingTapped: ITrackingAction;
  @Input() trackingSelected: ITrackingAction;
  @Input() trackingAdditionalProperties: any;
  @Input() horizontalAlignment: DropdownHorizontalAlignmentsEnum =
    DropdownHorizontalAlignmentsEnum.left;
  @Input() verticalAlignment: DropdownVerticalAlignmentsEnum =
    DropdownVerticalAlignmentsEnum.bottom;
  @Input() dynamicVAlignment: boolean;
  @Input() formControl: AbstractControl;

  @Output() onClickIcon: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _selectAllOption: IMultiSelectOption;
  private _selectAllFormControl: FormControl;
  private _valuesFormArray: FormArray;
  private _valuesFormArraySub: Subscription;

  dropdownValues: List<List<IMultiSelectOption>>;
  isDisabled: boolean;
  hasValue: boolean;
  selectedOptionsLabel: string;
  counter: number;

  constructor(
    private _multiSelectFieldBuilder: MultiSelectFieldBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingService: I2gNgTrackingService,
    translateService: TranslateService
  ) {
    super(_changeDetectorRef, translateService);
  }

  ngOnChanges(changes) {
    if (
      (changes.values ||
        changes.selectAllLabel ||
        changes.selectAllTrackingValue) &&
      this.values
    ) {
      this._selectAllOption = this._isSelectAllEnabled()
        ? this._multiSelectFieldBuilder.buildSelectAllOption(
            this.selectAllLabel,
            this.values,
            this.selectAllTrackingValue
          )
        : null;

      this._updateFormControls(this.values);

      this.dropdownValues = this._multiSelectFieldBuilder.buildDropdownValues(
        this._selectAllOption,
        this._selectAllFormControl,
        this.values,
        this._valuesFormArray
      );

      this._updateSelectedLabels();
    }
  }

  ngOnInit() {
    this.fieldControl = this.formControl;

    super.ngOnInit();
  }

  writeValue(modelValue: any) {
    if (!isUndefined(modelValue) && this.values) {
      const values = Array.from(modelValue);

      const formValues = this.values
        .map((selectOption: IMultiSelectOption) =>
          values.some((value) => isEqual(value, selectOption.value))
        )
        .toArray();

      this._valuesFormArray.reset(formValues, { emitEvent: false });

      if (this._isSelectAllEnabled()) {
        this._selectAllFormControl.setValue(
          every(this._valuesFormArray.getRawValue()),
          {
            emitEvent: false,
          }
        );
      }

      this._updateSelectedLabels();
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;

    this._changeDetectorRef.markForCheck();
  }

  onSelect(selectOption: IMultiSelectOption) {
    if (
      this.blocker &&
      (isUndefined(selectOption.block) || selectOption.block)
    ) {
      this.blocker(selectOption).then((result: IMultiSelectOption) => {
        if (result) {
          this._setSelectedOption(result);

          this.propagateChange(this._selectedValues());

          this._trackSelectedValues();
        }
      });
    } else {
      this._setSelectedOption(selectOption);

      this.propagateChange(this._selectedValues());

      this._trackSelectedValues();
    }
  }

  private _isSelectAllEnabled(): boolean {
    return !isNil(this.selectAllLabel);
  }

  private _selectedOptions(): List<IMultiSelectOption> {
    const selectedValues = this._valuesFormArray.getRawValue();

    return this.values
      ? (this.values.filter(
          (_, key: number) => selectedValues[key]
        ) as List<IMultiSelectOption>)
      : List<IMultiSelectOption>([]);
  }

  private _selectedValues(): any[] {
    return this._selectedOptions()
      .map((selectOption: IMultiSelectOption) => selectOption.value)
      .toArray();
  }

  private _selectedKeys(): List<string> {
    return this._selectedOptions().map(
      (selectOption: IMultiSelectOption) => selectOption.key
    ) as List<string>;
  }

  private _updateFormControls(values: List<IMultiSelectOption> = List()) {
    if (!this._valuesFormArray) {
      this._valuesFormArray = this._multiSelectFieldBuilder.buildValuesFormArray(
        values
      );

      this._valuesFormArraySub = this._selectAllOnMultiSelectValuesSubscribe();
    } else {
      const selectedValues = this._selectedValues();
      const formValues = values.map((selectOption: IMultiSelectOption) =>
        selectedValues.some((value) => isEqual(value, selectOption.value))
      );

      this._valuesFormArraySub.unsubscribe();

      while (this._valuesFormArray.length) {
        this._valuesFormArray.removeAt(0);
      }

      formValues.forEach((formValue) =>
        this._valuesFormArray.push(
          this._multiSelectFieldBuilder.buildValueFormControl(formValue)
        )
      );

      this._valuesFormArraySub = this._selectAllOnMultiSelectValuesSubscribe();

      this.propagateChange(selectedValues);
    }

    if (this._isSelectAllEnabled()) {
      const allSelected = every(this._valuesFormArray.getRawValue());

      if (!this._selectAllFormControl) {
        this._selectAllFormControl = this._multiSelectFieldBuilder.buildValueFormControl(
          allSelected
        );

        // check/uncheck all multi-select options when user click on select_all option
        this._selectAllFormControl.valueChanges
          .pipe(
            filter(() => this._isSelectAllEnabled()),
            takeUntil(this.destroyed)
          )
          .subscribe((selected) => {
            const allValues = fill(
              Array(this._valuesFormArray.controls.length),
              selected
            );

            // keep default value selected
            if (!selected && !isUndefined(this.defaultValue)) {
              const index = this.values.findIndex(
                (option: IMultiSelectOption) =>
                  option.value === this.defaultValue
              );

              allValues[index] = true;
            }

            this._valuesFormArray.setValue(allValues, { emitEvent: false });
          });
      } else {
        this._selectAllFormControl.setValue(allSelected, { emitEvent: false });
      }
    }
  }

  /**
   * Check/uncheck select_all option based on if all other multi-select options are checked
   */
  private _selectAllOnMultiSelectValuesSubscribe(): Subscription {
    return this._valuesFormArray.valueChanges
      .pipe(
        filter(() => this._isSelectAllEnabled()),
        takeUntil(this.destroyed)
      )
      .subscribe((valuesArray) => {
        const filteredValues = valuesArray.filter((value) => !isNil(value));
        const isAllFalse = !some(filteredValues);

        let isAllTrue = every(filteredValues);

        // keep default value selected
        if (isAllFalse && !isUndefined(this.defaultValue)) {
          const allValues = fill(
            Array(this._valuesFormArray.controls.length),
            false
          );
          const index = this.values.findIndex(
            (option: IMultiSelectOption) => option.value === this.defaultValue
          );

          allValues[index] = true;
          isAllTrue = false;

          this._valuesFormArray.setValue(allValues, { emitEvent: false });
        }

        this._selectAllFormControl.setValue(isAllTrue, { emitEvent: false });
      });
  }

  private _updateSelectedLabels() {
    const selectedKeys: List<string> = this._selectedKeys();
    const selectedKeysLength = selectedKeys.size;

    this.selectedOptionsLabel =
      selectedKeysLength > 0 ? selectedKeys.join(', ') : null;
    this.counter = selectedKeysLength > 1 ? selectedKeysLength : null;

    this._changeDetectorRef.markForCheck();
  }

  private _setSelectedOption(option: IMultiSelectOption) {
    const valueControl = option.valueControl;

    if (valueControl) {
      valueControl.setValue(!valueControl.value);

      this.formControl.markAsDirty();

      this._updateSelectedLabels();
    }
  }

  private _trackSelectedValues() {
    if (this.trackingObject && this.trackingSelected) {
      const trackingValue =
        this._isSelectAllEnabled() && this._selectAllFormControl.value
          ? this.selectAllTrackingValue
          : this._selectedOptions()
              .map((option: IMultiSelectOption) => option.trackingValue)
              .join(',');

      if (!!trackingValue) {
        this._trackingService.trackEvent(
          this.trackingObject,
          this.trackingSelected.setValue(trackingValue),
          this.trackingAdditionalProperties
        );
      }
    }
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
